import { api } from "@/api/client";
import { InfoDot, PanelHelp } from "@/components/PanelHelp";
import { Staleness } from "@/components/Staleness";
import { useECharts } from "@/hooks/useECharts";
import { usePoll } from "@/hooks/usePoll";
import { t } from "@/i18n";
import { formatInt } from "@/lib/format";
import { includeTestSessions, peakWindow, selectedTenant } from "@/state/filters";
import { ActivityPanel } from "./ActivityPanel";

const HOUR_LABELS = Array.from({ length: 24 }, (_, i) => `${i}h`);

// Mon..Sun in the active language, from one comma-joined dictionary entry.
const weekdayLabels = (): string[] => t("weekday.short").split(",");

export function PeakView() {
  const { data, loading, error, lastFetched, refetch } = usePoll(
    (s) =>
      api.peak(
        {
          window: peakWindow.value,
          includeTest: includeTestSessions.value,
          tenant: selectedTenant.value,
        },
        s,
      ),
    [peakWindow.value, includeTestSessions.value, selectedTenant.value],
  );

  const days = weekdayLabels();

  const heatmapData: [number, number, number][] = [];
  if (data) {
    for (let day = 0; day < 7; day++) {
      for (let hour = 0; hour < 24; hour++) {
        heatmapData.push([hour, day, data.hour_weekday_heatmap[day][hour]]);
      }
    }
  }
  const heatmapMax = data
    ? Math.max(1, ...heatmapData.map((d) => d[2]))
    : 1;

  const heatmapOption = data
    ? {
        title: { text: t("peak.chart.heatmap"), left: "center" },
        tooltip: {
          position: "top",
          formatter: (p: { value: [number, number, number] }) =>
            t("peak.tooltipTurns", {
              day: days[p.value[1]],
              hour: HOUR_LABELS[p.value[0]],
              n: formatInt(p.value[2]),
            }),
        },
        grid: { top: 60, bottom: 40, left: 50, right: 24 },
        xAxis: { type: "category", data: HOUR_LABELS, splitArea: { show: true } },
        yAxis: { type: "category", data: days, splitArea: { show: true } },
        visualMap: {
          min: 0,
          max: heatmapMax,
          orient: "horizontal",
          bottom: 0,
          calculable: true,
          inRange: { color: ["#e6d2b4", "#fab400", "#002da5"] },
        },
        series: [
          {
            type: "heatmap",
            data: heatmapData,
            label: { show: false },
          },
        ],
      }
    : null;
  const heatmapRef = useECharts(heatmapOption);

  const hourOption = data
    ? {
        title: { text: t("peak.chart.hour"), left: "center", textStyle: { fontSize: 14 } },
        tooltip: { trigger: "axis" },
        xAxis: { type: "category", data: HOUR_LABELS },
        yAxis: { type: "value" },
        series: [
          {
            type: "bar",
            data: data.hour_of_day_histogram,
            itemStyle: { color: "#002da5" },
          },
        ],
      }
    : null;
  const hourRef = useECharts(hourOption);

  const weekdayOption = data
    ? {
        title: { text: t("peak.chart.weekday"), left: "center", textStyle: { fontSize: 14 } },
        tooltip: { trigger: "axis" },
        xAxis: { type: "category", data: days },
        yAxis: { type: "value" },
        series: [
          {
            type: "bar",
            data: data.weekday_histogram,
            itemStyle: { color: "#fab400" },
          },
        ],
      }
    : null;
  const weekdayRef = useECharts(weekdayOption);

  return (
    <div style="display: grid; gap: 24px;">
      <div style="display: flex; align-items: center; gap: 24px;">
        <Staleness
          lastFetched={lastFetched}
          loading={loading}
          error={error}
          onRefresh={refetch}
        />
      </div>

      <div class="grid grid-3">
        <div class="card">
          <h3>{t("peak.rightNow")}</h3>
          <div class="kpi">{formatInt(data?.turns_last_5min ?? 0)}</div>
          <div class="kpi-sub">{t("peak.turns")}</div>
        </div>
        <div class="card">
          <h3>{t("peak.baseline")}</h3>
          <div class="kpi">{formatInt(data?.median_5min_for_same_hour_of_week ?? 0)}</div>
          <div class="kpi-sub">{t("peak.baselineSub")}</div>
        </div>
        <div class="card">
          <h3>
            {t("peak.rateVsBaseline")} <InfoDot k="peak.rateVsBaseline" />
          </h3>
          <div class="kpi">
            {data?.rate_vs_baseline == null
              ? "—"
              : `${data.rate_vs_baseline.toFixed(2)}x`}
          </div>
          <div class="kpi-sub">
            {data?.rate_vs_baseline == null
              ? t("peak.noBaseline")
              : data.rate_vs_baseline > 1.5
                ? t("peak.above")
                : t("peak.within")}
          </div>
        </div>
      </div>

      <ActivityPanel />

      <section class="card">
        <h2>{t("peak.whenSpike")}</h2>
        <PanelHelp k="peak.heatmap" />
        <div ref={heatmapRef} class="chart chart-tall" />
      </section>

      <div class="grid grid-3">
        <section class="card">
          <div ref={hourRef} class="chart" />
        </section>
        <section class="card">
          <div ref={weekdayRef} class="chart" />
        </section>
        <section class="card">
          <h3>
            {t("peak.localeHour")} <InfoDot k="peak.localeHour" />
          </h3>
          {data &&
            Object.entries(data.locale_hour_heatmap).map(([locale, bins]) => (
              <div key={locale} style="margin-bottom: 8px;">
                <div class="muted">{locale}</div>
                <div style="display: flex; gap: 2px; align-items: end; height: 60px;">
                  {bins.map((v, h) => {
                    const max = Math.max(1, ...bins);
                    return (
                      <div
                        key={h}
                        title={`${h}h: ${v}`}
                        style={`flex: 1; background: var(--brand-blue); height: ${
                          (v / max) * 100
                        }%; opacity: ${v ? 0.8 : 0.15}; border-radius: 2px;`}
                      />
                    );
                  })}
                </div>
              </div>
            ))}
          {(!data || Object.keys(data.locale_hour_heatmap).length === 0) && (
            <div class="muted">{t("peak.noLocale")}</div>
          )}
        </section>
      </div>
    </div>
  );
}
